import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion'; // Import framer-motion

function Ministries() {
  const { t } = useTranslation();

  const ministries = [
    {
      title: t('ministries.crusadesTitle'),
      description: t('ministries.crusadesDescription'),
      image: '/assets/images/crusades.jpg',
      link: '/events',
      linkText: t('ministries.crusadesLink'),
    },
    {
      title: t('ministries.discipleshipTitle'),
      description: t('ministries.discipleshipDescription'),
      image: '/assets/images/discipleship1.jpg',
      link: '/about',
      linkText: t('ministries.discipleshipLink'),
    },
    {
      title: t('ministries.youthTitle'),
      description: t('ministries.youthDescription'),
      image: '/assets/images/youth.jpg',
      link: '/contact',
      linkText: t('ministries.youthLink'),
    },
    {
      title: t('ministries.outreachTitle'),
      description: t('ministries.outreachDescription'),
      image: '/assets/images/outreach.jpg',
      link: '/donate',
      linkText: t('ministries.outreachLink'),
    },
  ];

  // Define the animation variants for the Ministries Section
  const ministriesVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.8, 
        ease: 'easeOut',
        staggerChildren: 0.2 // Stagger the animation of child elements (ministry cards)
      } 
    },
  };

  // Animation variants for individual ministry cards
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t('ministries.seoTitle')}</title>
        <meta name="description" content={t('ministries.seoDescription')} />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/assets/images/ministries.jpeg')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('ministries.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl">
            {t('ministries.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Ministries Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={ministriesVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-12">
              {t('ministries.sectionTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {ministries.map((ministry, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300"
                >
                  <img
                    src={ministry.image}
                    alt={ministry.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => (e.target.src = 'https://via.placeholder.com/400x200?text=Ministry')}
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-textPrimary mb-2">
                      {ministry.title}
                    </h3>
                    <p className="text-textPrimary mb-4">
                      {ministry.description}
                    </p>
                    <Link
                      to={ministry.link}
                      className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      {ministry.linkText}
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-primary text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold mb-6">
            {t('ministries.ctaTitle')}
          </h2>
          <Link
            to="/contact"
            className="bg-accent text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-yellow-600 transition-colors duration-200"
          >
            {t('ministries.ctaButton')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Ministries;